import { useEffect, useState } from "react";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import ModalFrame from "../../components/ModalFrame";
import { getHighestGroupNumber } from "./coursePromptReviewGroups/coursePromptGroupsUtil";
import { CoursePromptReviewGroup } from "./coursePromptReviewGroups/coursePromptReviewGroupsApiSlice";
import { EditPeerReviewGroupForm } from "./EditPeerReviewGroupForm";
import {
	useLazyAddGroupQuery,
	useAddGroupMutation,
	EditReviewGroupModel,
	ReviewGroupWriterModel,
} from "./peerReviewGroupsApiSlice";

interface CreatePeerReviewGroupButtonProps {
	disabled: boolean;
	makingCoursePromptGroup: boolean;
	groupNames?: string[];
	availablePool?: ReviewGroupWriterModel[];
	onCreateCoursePromptGroup?: (newGroup: CoursePromptReviewGroup) => void;
	onClick?: () => void;
}
export function CreatePeerReviewGroupButton(props: CreatePeerReviewGroupButtonProps) {
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const handleClick = () => {
		props.onClick?.();
		setIsDialogOpen(true);
	};

	const handleDialogClose = () => setIsDialogOpen(false);

	return (
		<>
			<Button
				id="create-group-button"
				className="btn btn-neutral mr-2"
				disabled={props.disabled}
				onClick={() => handleClick()}>
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add Group
				</span>
			</Button>

			{isDialogOpen && (
				<CreatePeerReviewGroupDialog
					isDialogOpen={isDialogOpen}
					makingCoursePromptGroup={props.makingCoursePromptGroup}
					groupNames={props.groupNames}
					availablePool={props.availablePool}
					onCreateCoursePromptGroup={props.onCreateCoursePromptGroup}
					onClose={handleDialogClose}
				/>
			)}
		</>
	);
}

interface CreatePeerReviewGroupDialogProps {
	isDialogOpen: boolean;
	makingCoursePromptGroup: boolean;
	groupNames?: string[];
	availablePool?: ReviewGroupWriterModel[];
	onCreateCoursePromptGroup?: (newGroup: CoursePromptReviewGroup) => void;
	onClose: () => void;
}

export function CreatePeerReviewGroupDialog(props: CreatePeerReviewGroupDialogProps) {
	const [addGroupData, setAddGroupData] = useState<EditReviewGroupModel>();
	const [callGetAddGroup] = useLazyAddGroupQuery();
	const [callPostAddGroup] = useAddGroupMutation();

	useEffect(() => {
		if (props.isDialogOpen) {
			if (props.makingCoursePromptGroup) {
				setAddGroupData({
					id: 0,
					name: `Prompt Group ${getHighestGroupNumber(props.groupNames ?? []) + 1}`,
					writerIds: [],
					writerPool: props.availablePool ?? [],
				});
			} else {
				callGetAddGroup()
					.unwrap()
					.then((result) => {
						setAddGroupData(result);
					})
					.catch((error: any) => {
						console.log(error);
					});
			}
		}
	}, [props.isDialogOpen]);

	function handleSubmit(model: EditReviewGroupModel) {
		var errorMessage = "";
		if (props.makingCoursePromptGroup) {
			// Create the new group object for course prompt groups
			const newGroup: CoursePromptReviewGroup = {
				id: model.id,
				uniqueId: crypto.randomUUID(),
				name: model.name,
				studentIds: model.writerIds,
			};

			if (props.onCreateCoursePromptGroup) {
				props.onCreateCoursePromptGroup(newGroup);
				props.onClose();
			}
		} else {
			callPostAddGroup(model)
				.unwrap()
				.then(() => {
					props.onClose();
				})
				.catch((error: any) => {
					console.log(error);
					errorMessage = error.data;
				});
		}
		return errorMessage;
	}

	return (
		addGroupData && (
			<ModalFrame
				open={props.isDialogOpen}
				title="Create Review Group"
				overflow="overflow-visible"
				onClose={() => props.onClose()}>
				<EditPeerReviewGroupForm
					initialGroupData={addGroupData}
					submitLabel="Create Group"
					onSubmit={handleSubmit}
					onClose={props.onClose}
				/>
			</ModalFrame>
		)
	);
}
